import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, API } from "../App";
import { useCurrency } from "../components/LanguageCurrencySelector";
import { Button } from "../components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import PayPalPayment from "../components/PayPalPayment";
import PayPalInstallment from "../components/PayPalInstallment";

const LOGO_URL = "/images/singleseen_logo_transparent.png";

const PricingPage = () => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState("premium"); // Default to premium (recommended)
  const [selectedDuration, setSelectedDuration] = useState("12"); // Default to 12 months (best value)
  const [paymentType, setPaymentType] = useState("installment"); // "upfront" or "installment"
  const [showPayment, setShowPayment] = useState(false);
  const [showInstallment, setShowInstallment] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  const handleSubscribe = async (packageId, isInstallment = false) => {
    if (!user) {
      toast.error(t('pricing.loginToSubscribe'));
      navigate("/login");
      return;
    }

    const currentPrice = pricing[selectedPlan][selectedDuration];
    const months = parseInt(selectedDuration);

    if (isInstallment && months > 1) {
      // Monthly installment subscription (committed to full term)
      setSelectedPackage({
        planId: `${selectedPlan}_${selectedDuration}_installment`,
        name: `${selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} - ${months} Month Plan`,
        monthlyAmount: currentPrice.perMonth,
        totalAmount: currentPrice.total,
        months: months,
        currency: "gbp",
        tier: selectedPlan
      });
      setShowInstallment(true);
    } else {
      // One-time upfront payment
      const packageDetails = getPackageDetails(packageId);
      setSelectedPackage({
        packageId,
        ...packageDetails
      });
      setShowPayment(true);
    }
  };

  const getPackageDetails = (packageId) => {
    const packages = {
      "basic_1": { name: "Basic - 1 Month", amount: 24.99, currency: "gbp" },
      "basic_3": { name: "Basic - 3 Months", amount: 49.99, currency: "gbp" },
      "basic_6": { name: "Basic - 6 Months", amount: 74.99, currency: "gbp" },
      "basic_12": { name: "Basic - 12 Months", amount: 99.99, currency: "gbp" },
      "premium_1": { name: "Premium - 1 Month", amount: 34.99, currency: "gbp" },
      "premium_3": { name: "Premium - 3 Months", amount: 69.99, currency: "gbp" },
      "premium_6": { name: "Premium - 6 Months", amount: 104.99, currency: "gbp" },
      "premium_12": { name: "Premium - 12 Months", amount: 139.99, currency: "gbp" },
      "connection": { name: "Connection Fee", amount: 9.99, currency: "gbp" },
      "contact_exchange": { name: "Contact Exchange", amount: 2.99, currency: "gbp" }
    };
    return packages[packageId] || { name: packageId, amount: 0, currency: "gbp" };
  };

  const handlePaymentSuccess = (result) => {
    setShowPayment(false);
    setSelectedPackage(null);
    navigate("/payment/success?direct=true");
  };

  const handlePaymentClose = () => {
    setShowPayment(false);
    setShowInstallment(false);
    setSelectedPackage(null);
  };

  const handleInstallmentSuccess = (result) => {
    setShowInstallment(false);
    setSelectedPackage(null);
    navigate("/payment/subscription-success?direct=true");
  };

  // Pricing data
  const pricing = {
    basic: {
      "1": { total: 24.99, perMonth: 24.99, save: 0 },
      "3": { total: 49.99, perMonth: 16.66, save: 33 },
      "6": { total: 74.99, perMonth: 12.50, save: 50 },
      "12": { total: 99.99, perMonth: 8.33, save: 67 }
    },
    premium: {
      "1": { total: 34.99, perMonth: 34.99, save: 0 },
      "3": { total: 69.99, perMonth: 23.33, save: 33 },
      "6": { total: 104.99, perMonth: 17.50, save: 50 },
      "12": { total: 139.99, perMonth: 11.67, save: 67 }
    }
  };

  // Feature comparison data - comprehensive list
  const features = [
    { 
      category: t('pricing.catProfile'),
      items: [
        { name: t('pricing.featCreateProfile'), free: true, basic: true, premium: true },
        { name: t('pricing.featBrowseProfiles'), free: true, basic: true, premium: true },
        { name: t('pricing.featBlurredPhotos'), free: true, basic: true, premium: true },
        { name: t('pricing.featFullPhotos'), free: false, basic: true, premium: true },
        { name: t('pricing.featOnlineNow'), free: false, basic: true, premium: true },
        { name: t('pricing.featAdvancedFilters'), free: false, basic: true, premium: true },
      ]
    },
    {
      category: t('pricing.catConnecting'),
      items: [
        { name: t('pricing.featSendInterests'), free: t('pricing.threePerDay'), basic: t('pricing.unlimited'), premium: t('pricing.unlimited') },
        { name: t('pricing.featPhotoAccess'), free: false, basic: true, premium: true },
        { name: t('pricing.featStartConvos'), free: false, basic: false, premium: true, highlight: true },
        { name: t('pricing.featChatMatches'), free: false, basic: false, premium: t('pricing.upTo5') },
      ]
    },
    {
      category: t('pricing.catVisibility'),
      items: [
        { name: t('pricing.featWhoViewed'), free: false, basic: false, premium: true },
        { name: t('pricing.featHighlighting'), free: false, basic: false, premium: true },
        { name: t('pricing.featPrioritySearch'), free: false, basic: false, premium: true },
        { name: t('pricing.featVerifiedBadge'), free: false, basic: true, premium: true },
      ]
    },
    {
      category: t('pricing.catSupport'),
      items: [
        { name: t('pricing.featHelpCenter'), free: true, basic: true, premium: true },
        { name: t('pricing.featEmailSupport'), free: true, basic: true, premium: true },
        { name: t('pricing.featPrioritySupport'), free: false, basic: false, premium: true },
      ]
    }
  ];

  const durations = [
    { value: "1", label: t('pricing.1month'), shortLabel: "1M" },
    { value: "3", label: t('pricing.3months'), shortLabel: "3M" },
    { value: "6", label: t('pricing.6months'), shortLabel: "6M" },
    { value: "12", label: t('pricing.12months'), shortLabel: "1Y", badge: t('pricing.bestValue') }
  ];

  const currentPrice = pricing[selectedPlan][selectedDuration];
  const packageId = `${selectedPlan}_${selectedDuration}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 via-white to-amber-50/30">
      {/* Header */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-rose-100/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-36">
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img src={LOGO_URL} alt="SingleSeen" className="h-36 w-auto" />
            </Link>

            <div className="flex items-center gap-4">
              {user ? (
                <Button 
                  onClick={() => navigate("/dashboard")}
                  className="bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 rounded-full px-6"
                >
                  {t('nav.dashboard')}
                </Button>
              ) : (
                <>
                  <Link to="/login" className="text-rose-600 hover:text-rose-800 font-medium">{t('nav.login')}</Link>
                  <Button 
                    onClick={() => navigate("/register")}
                    className="bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 rounded-full px-6"
                  >
                    {t('nav.getStarted')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      <main className="py-12 px-4 sm:px-6 lg:px-8 pt-40">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <span className="text-rose-500 font-medium tracking-wide uppercase text-sm mb-4 block">
              {t('pricing.badge')}
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl text-rose-900 mb-4" data-testid="pricing-heading">
              {t('pricing.title')}
            </h1>
            <p className="text-rose-600 text-lg max-w-2xl mx-auto">
              {t('pricing.subtitle')}
            </p>
          </div>

          {/* Payment Type Toggle - Pay Upfront vs Monthly Installments */}
          <div className="flex justify-center mb-6">
            <div className="bg-white p-1.5 rounded-full inline-flex shadow-md border border-rose-100" role="tablist" aria-label="Select payment type">
              <button
                onClick={() => setPaymentType("installment")}
                className={`relative px-6 py-2.5 rounded-full font-medium transition-all text-sm ${
                  paymentType === "installment"
                    ? "bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md"
                    : "text-rose-500 hover:text-rose-700"
                }`}
                role="tab"
                aria-selected={paymentType === "installment"}
                data-testid="payment-type-installment"
              >
                {t('pricing.payMonthly')}
                <span className="absolute -top-2 -right-2 bg-emerald-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {t('pricing.easy')}
                </span>
              </button>
              <button
                onClick={() => setPaymentType("upfront")}
                className={`relative px-6 py-2.5 rounded-full font-medium transition-all text-sm ${
                  paymentType === "upfront"
                    ? "bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-md"
                    : "text-rose-500 hover:text-rose-700"
                }`}
                role="tab"
                aria-selected={paymentType === "upfront"}
                data-testid="payment-type-upfront"
              >
                {t('pricing.payUpfront')}
              </button>
            </div>
          </div>

          {/* Payment Type Description */}
          <div className="text-center mb-8">
            {paymentType === "installment" ? (
              <p className="text-emerald-600 text-sm">
                {t('pricing.installmentDesc')}
              </p>
            ) : (
              <p className="text-rose-500 text-sm">
                {t('pricing.upfrontDesc')}
              </p>
            )}
          </div>

          {/* Plan Toggle - Basic vs Premium */}
          <div className="flex justify-center mb-8">
            <div className="bg-rose-100/50 p-1.5 rounded-full inline-flex" role="tablist" aria-label="Select plan type">
              <button
                onClick={() => setSelectedPlan("basic")}
                className={`relative px-8 py-3 rounded-full font-medium transition-all ${
                  selectedPlan === "basic"
                    ? "bg-white text-rose-900 shadow-md"
                    : "text-rose-500 hover:text-rose-700"
                }`}
                role="tab"
                aria-selected={selectedPlan === "basic"}
                data-testid="plan-toggle-basic"
              >
                <span className="text-lg font-light mr-2">01</span>
                {t('pricing.basic')}
              </button>
              <button
                onClick={() => setSelectedPlan("premium")}
                className={`relative px-8 py-3 rounded-full font-medium transition-all ${
                  selectedPlan === "premium"
                    ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md"
                    : "text-rose-500 hover:text-rose-700"
                }`}
                role="tab"
                aria-selected={selectedPlan === "premium"}
                data-testid="plan-toggle-premium"
              >
                <span className="text-lg font-light mr-2">02</span>
                {t('pricing.premium')}
                <span className="absolute -top-2 -right-2 bg-rose-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {t('pricing.popular')}
                </span>
              </button>
            </div>
          </div>

          {/* Duration Selector - Always shown */}
          <div className="flex justify-center gap-3 mb-10 flex-wrap">
            {durations.map((d) => (
              <button
                key={d.value}
                onClick={() => setSelectedDuration(d.value)}
                className={`relative px-6 py-3 rounded-xl font-medium transition-all border-2 ${
                  selectedDuration === d.value
                    ? paymentType === "installment"
                      ? "border-emerald-500 bg-emerald-50 text-emerald-900"
                      : selectedPlan === "premium"
                        ? "border-amber-500 bg-amber-50 text-amber-900"
                        : "border-rose-500 bg-rose-50 text-rose-900"
                    : "border-stone-200 bg-white text-stone-600 hover:border-stone-300"
                }`}
                data-testid={`duration-${d.value}`}
              >
                <span className="block text-sm">{d.label}</span>
                {pricing[selectedPlan][d.value].save > 0 && (
                  <span className={`block text-xs mt-0.5 ${
                    selectedDuration === d.value 
                      ? paymentType === "installment" ? "text-emerald-600" : selectedPlan === "premium" ? "text-amber-600" : "text-rose-600"
                      : "text-green-600"
                  }`}>
                    {t('pricing.save', { percent: pricing[selectedPlan][d.value].save })}
                  </span>
                )}
                {d.badge && (
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full whitespace-nowrap">
                    {d.badge}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Installment info - show when installment payment + multi-month plan */}
          {paymentType === "installment" && parseInt(selectedDuration) > 1 && (
            <div className="flex justify-center mb-10">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 max-w-lg text-center">
                <p className="text-emerald-700 font-medium mb-1">{t('pricing.installmentTitle')}</p>
                <p className="text-emerald-600 text-sm">
                  {t('pricing.installmentInfo', { 
                    months: selectedDuration,
                    monthlyPrice: formatPrice(pricing[selectedPlan][selectedDuration].perMonth),
                    totalPrice: formatPrice(pricing[selectedPlan][selectedDuration].total)
                  })}
                </p>
              </div>
            </div>
          )}

          {/* Selected Plan Card */}
          <div className={`max-w-md mx-auto mb-16 rounded-3xl p-8 shadow-xl border-2 ${
            paymentType === "installment"
              ? "bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-400"
              : selectedPlan === "premium"
                ? "bg-gradient-to-br from-amber-50 to-orange-50 border-amber-400"
                : "bg-white border-rose-200"
          }`} data-testid="selected-plan-card">
            <div className="flex items-center gap-4 mb-6">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
                paymentType === "installment"
                  ? "bg-gradient-to-br from-emerald-500 to-teal-500 text-white"
                  : selectedPlan === "premium"
                    ? "bg-gradient-to-br from-amber-500 to-orange-500 text-white"
                    : "bg-gradient-to-br from-rose-500 to-pink-500 text-white"
              }`}>
                <span className="text-2xl font-serif">{selectedPlan === "premium" ? "P" : "B"}</span>
              </div>
              <div>
                <h2 className="font-serif text-3xl text-rose-900 capitalize">
                  {t(`pricing.${selectedPlan}`)}
                </h2>
                <p className="text-rose-500">
                  {selectedDuration === "1" 
                    ? t('pricing.oneMonthAccess')
                    : t('pricing.monthsAccess', { months: selectedDuration })}
                </p>
              </div>
            </div>

            {/* Price Display */}
            <div className={`p-6 rounded-2xl mb-6 ${
              paymentType === "installment" ? "bg-white/80" : selectedPlan === "premium" ? "bg-white/80" : "bg-rose-50"
            }`}>
              <div className="flex items-baseline justify-center gap-1">
                <span className={`font-serif text-6xl ${paymentType === "installment" ? "text-emerald-700" : "text-rose-900"}`}>
                  {formatPrice(currentPrice.perMonth)}
                </span>
                <span className={`text-lg ${paymentType === "installment" ? "text-emerald-600" : "text-rose-500"}`}>
                  {t('common.perMonth')}
                </span>
              </div>
              <div className="text-center mt-3 space-y-1">
                {paymentType === "installment" && parseInt(selectedDuration) > 1 ? (
                  <>
                    <p className="text-emerald-600 font-medium">
                      {t('pricing.installmentPayment', { months: selectedDuration })}
                    </p>
                    <p className="text-emerald-500 text-sm">
                      {t('pricing.totalCommitment', { total: formatPrice(currentPrice.total) })}
                    </p>
                  </>
                ) : (
                  <>
                    <p className={paymentType === "installment" ? "text-emerald-600" : "text-rose-600"}>
                      {selectedDuration === "1" 
                        ? t('pricing.singlePayment', { price: formatPrice(currentPrice.total) })
                        : t('pricing.billedAs', { price: formatPrice(currentPrice.total), duration: selectedDuration, period: t('common.months') })}
                    </p>
                  </>
                )}
                {currentPrice.save > 0 && (
                  <p className="text-green-600 font-medium">
                    {t('pricing.saveVsMonthly', { percent: currentPrice.save })}
                  </p>
                )}
              </div>
            </div>

            {/* CTA Button */}
            <Button
              onClick={() => handleSubscribe(packageId, paymentType === "installment" && parseInt(selectedDuration) > 1)}
              disabled={loading === packageId}
              className={`w-full rounded-full h-14 text-lg font-semibold shadow-lg ${
                paymentType === "installment"
                  ? "bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
                  : selectedPlan === "premium"
                    ? "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                    : "bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white"
              }`}
              data-testid="subscribe-btn"
            >
              {loading === packageId 
                ? t('common.processing') 
                : paymentType === "installment" && parseInt(selectedDuration) > 1
                  ? t('pricing.startInstallments', { price: formatPrice(currentPrice.perMonth) })
                  : t('pricing.payNow', { price: formatPrice(currentPrice.total) })}
            </Button>

            <p className={`text-center text-sm mt-4 ${paymentType === "installment" ? "text-emerald-500" : "text-rose-500"}`}>
              {paymentType === "installment" && parseInt(selectedDuration) > 1
                ? t('pricing.committedInstallments', { months: selectedDuration })
                : t('pricing.securePaymentPaypal')}
            </p>
          </div>

          {/* Feature Comparison Table */}
          <div className="bg-white rounded-3xl border border-rose-100 shadow-sm overflow-hidden mb-12" data-testid="comparison-table">
            <div className="p-6 border-b border-rose-100">
              <h2 className="font-serif text-2xl text-rose-900 text-center">{t('pricing.fullComparison')}</h2>
              <p className="text-rose-500 text-center mt-2">{t('pricing.seeExactly')}</p>
            </div>

            {/* Table Header */}
            <div className="grid grid-cols-4 bg-rose-50 border-b border-rose-100">
              <div className="p-4 font-medium text-rose-700">{t('pricing.feature')}</div>
              <div className="p-4 text-center">
                <span className="text-rose-500 font-medium">{t('common.free')}</span>
              </div>
              <div className="p-4 text-center">
                <div className="inline-flex items-center gap-1.5 text-rose-700 font-semibold">
                  <span className="text-sm font-light">01</span>
                  {t('pricing.basic')}
                </div>
              </div>
              <div className="p-4 text-center bg-amber-50">
                <div className="inline-flex items-center gap-1.5 text-amber-700 font-semibold">
                  <span className="text-sm font-light">02</span>
                  {t('pricing.premium')}
                </div>
              </div>
            </div>

            {/* Table Body */}
            {features.map((section, sIdx) => (
              <div key={sIdx}>
                {/* Category Header */}
                <div className="grid grid-cols-4 bg-rose-50/50">
                  <div className="col-span-4 p-3 px-4">
                    <span className="text-sm font-semibold text-rose-900 uppercase tracking-wide">{section.category}</span>
                  </div>
                </div>
                
                {/* Category Items */}
                {section.items.map((item, iIdx) => (
                  <div 
                    key={iIdx} 
                    className={`grid grid-cols-4 border-b border-rose-100 ${item.highlight ? 'bg-amber-50/30' : ''}`}
                  >
                    <div className="p-4 text-rose-700 flex items-center">
                      {item.name}
                      {item.highlight && (
                        <span className="ml-2 bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">Key</span>
                      )}
                    </div>
                    <div className="p-4 flex justify-center items-center">
                      {renderFeatureValue(item.free, "rose")}
                    </div>
                    <div className="p-4 flex justify-center items-center">
                      {renderFeatureValue(item.basic, "rose")}
                    </div>
                    <div className="p-4 flex justify-center items-center bg-amber-50/50">
                      {renderFeatureValue(item.premium, "amber")}
                    </div>
                  </div>
                ))}
              </div>
            ))}

            {/* Pricing Row */}
            <div className="grid grid-cols-4 bg-rose-100/50 border-t-2 border-rose-200">
              <div className="p-4 font-semibold text-rose-900">{t('pricing.startingPrice')}</div>
              <div className="p-4 text-center">
                <span className="text-rose-600 font-semibold">{formatPrice(0)}</span>
                <span className="block text-xs text-rose-500">{t('common.forever')}</span>
              </div>
              <div className="p-4 text-center">
                <span className="text-rose-700 font-bold text-lg">{formatPrice(8.33)}</span>
                <span className="block text-xs text-rose-500">{t('common.perMonth')} ({t('common.annual')})</span>
              </div>
              <div className="p-4 text-center bg-amber-100/50">
                <span className="text-amber-700 font-bold text-lg">{formatPrice(11.67)}</span>
                <span className="block text-xs text-rose-500">{t('common.perMonth')} ({t('common.annual')})</span>
              </div>
            </div>

            {/* CTA Row */}
            <div className="grid grid-cols-4 bg-white">
              <div className="p-4"></div>
              <div className="p-4 text-center">
                <Button
                  onClick={() => navigate("/register")}
                  variant="outline"
                  className="border-rose-300 text-rose-600 hover:bg-rose-50 rounded-full"
                >
                  {t('pricing.signUpFree')}
                </Button>
              </div>
              <div className="p-4 text-center">
                <Button
                  onClick={() => {
                    setSelectedPlan("basic");
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-full"
                >
                  {t('pricing.getBasic')}
                </Button>
              </div>
              <div className="p-4 text-center bg-amber-50/50">
                <Button
                  onClick={() => {
                    setSelectedPlan("premium");
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-full"
                >
                  {t('pricing.getPremium')}
                </Button>
              </div>
            </div>
          </div>

          {/* One-Time Purchases */}
          <div className="bg-white rounded-3xl border border-rose-100 p-8 mb-12" data-testid="one-time-section">
            <h2 className="font-serif text-2xl text-rose-900 mb-2 text-center">{t('pricing.payAsYouGo')}</h2>
            <p className="text-rose-500 mb-8 text-center">{t('pricing.payAsYouGoDesc')}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <div className="flex items-center justify-between p-6 bg-gradient-to-r from-rose-50 to-pink-50 rounded-2xl border border-rose-100">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-sm border border-rose-100">
                    <span className="text-xl font-light text-rose-500">01</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-rose-900">{t('pricing.connectionFee')}</h4>
                    <p className="text-rose-500 text-sm">{t('pricing.connectionFeeDesc')}</p>
                  </div>
                </div>
                <Button
                  onClick={() => handleSubscribe("connection")}
                  disabled={loading === "connection"}
                  className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-full px-6"
                  data-testid="buy-connection"
                >
                  {formatPrice(9.99)}
                </Button>
              </div>

              <div className="flex items-center justify-between p-6 bg-gradient-to-r from-rose-50 to-pink-50 rounded-2xl border border-rose-100">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-sm border border-rose-100">
                    <span className="text-xl font-light text-rose-500">02</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-rose-900">{t('pricing.contactExchange')}</h4>
                    <p className="text-rose-500 text-sm">{t('pricing.contactExchangeDesc')}</p>
                  </div>
                </div>
                <Button
                  onClick={() => handleSubscribe("contact_exchange")}
                  disabled={loading === "contact_exchange"}
                  className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-full px-6"
                  data-testid="buy-contact"
                >
                  {formatPrice(2.99)}
                </Button>
              </div>
            </div>
          </div>

          {/* Trust Section */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-8 flex-wrap">
              <div className="flex items-center gap-2 text-rose-500">
                <span className="text-lg font-light">01</span>
                <span>{t('pricing.securePayment')}</span>
              </div>
              <div className="flex items-center gap-2 text-rose-500">
                <span className="text-lg font-light">02</span>
                <span>{t('pricing.idVerification')}</span>
              </div>
              <div className="flex items-center gap-2 text-rose-500">
                <span className="text-lg font-light">03</span>
                <span>{t('pricing.realProfiles')}</span>
              </div>
            </div>
            <p className="text-rose-400 text-sm mt-4">
              {t('pricing.securePaymentNote')}
            </p>
          </div>
        </div>
      </main>

      {/* PayPal Payment Modal (One-time/Upfront) */}
      {showPayment && selectedPackage && (
        <PayPalPayment
          packageId={selectedPackage.packageId}
          packageName={selectedPackage.name}
          amount={selectedPackage.amount}
          currency={selectedPackage.currency}
          onClose={handlePaymentClose}
          onSuccess={handlePaymentSuccess}
        />
      )}

      {/* PayPal Installment Modal (Monthly payments for committed term) */}
      {showInstallment && selectedPackage && (
        <PayPalInstallment
          planId={selectedPackage.planId}
          planName={selectedPackage.name}
          monthlyAmount={selectedPackage.monthlyAmount}
          totalAmount={selectedPackage.totalAmount}
          months={selectedPackage.months}
          currency={selectedPackage.currency}
          tier={selectedPackage.tier}
          onClose={handlePaymentClose}
          onSuccess={handleInstallmentSuccess}
        />
      )}
    </div>
  );
};

// Helper function to render feature values in the comparison table (no icons)
const renderFeatureValue = (value, color) => {
  if (value === true) {
    return (
      <div className={`w-6 h-6 rounded-full ${color === "amber" ? "bg-amber-100" : "bg-rose-100"} flex items-center justify-center`}>
        <span className={`text-sm font-bold ${color === "amber" ? "text-amber-600" : "text-rose-600"}`}>✓</span>
      </div>
    );
  }
  if (value === false) {
    return <span className="text-rose-300 text-lg">—</span>;
  }
  // String value (like "3/day", "Unlimited", "Up to 5")
  return (
    <span className={`text-sm font-medium ${
      color === "amber" ? "text-amber-700" : "text-rose-700"
    }`}>
      {value}
    </span>
  );
};

export default PricingPage;
